import { Typography } from 'antd';
import { PhoneFilledIcon } from 'assets';
import dayjs from 'dayjs';
import React, { memo, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { CallSessionDto } from 'types';
import { CALL_SESSION_OPTION, CALL_SESSION_TYPE, CALL_SOURCE_OPTION } from 'utils';

type AICallItemProps = {
  item: CallSessionDto;
  selected?: boolean;
  onSelect?: () => void;
};

// Helper chuyển hex (#RRGGBB) sang rgba với alpha; nếu không đúng định dạng hex trả về màu gốc.
const withAlpha = (hex: string | undefined, alpha: number) => {
  if (!hex || !/^#([0-9a-fA-F]{6})$/.test(hex)) return hex || '';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const AICallItemSymbol: React.FC<AICallItemProps> = ({ item }) => {
  const [blink, setBlink] = React.useState(false);

  useEffect(() => {
    if (item.isAlerted) {
      const interval = setInterval(() => {
        setBlink((prev) => !prev);
      }, 500);

      return () => clearInterval(interval);
    }
  }, [item.isAlerted]);
  const isBlink = blink;

  const optionData = item.isAlerted
    ? CALL_SESSION_OPTION[CALL_SESSION_TYPE.ERROR]
    : CALL_SESSION_OPTION[CALL_SESSION_TYPE.NORMAL];

  const strokeColor = optionData?.color;
  // Các mức alpha cho nhiều lớp bóng (glow) ngoài & trong.
  const shadowStrong = withAlpha(optionData?.color, 0.4); // lớp gần, đậm
  const shadowMid = withAlpha(optionData?.color, 0.2); // lớp trung bình
  const shadowSoft = withAlpha(optionData?.color, 0.1); // lớp xa, mềm
  const innerGlow = withAlpha(optionData?.color, 0.22); // tạo cảm giác loang đỏ bên trong
  // 1) Viền mỏng 1px (inner stroke) luôn hiển thị
  const baseStroke = `inset 0 0 0 1px ${strokeColor}`;
  // 2) Các lớp glow chỉ bật khi blink
  const glowShadow = `inset 0 0 18px 4px ${innerGlow}, 0 3px 6px 0 ${shadowStrong}, 0 8px 16px 2px ${shadowMid}, 0 14px 28px 4px ${shadowSoft}`;
  const boxShadow = `${baseStroke}, ${glowShadow}`;

  return (
    <div
      style={{
        // Stroke 1px luôn hiển thị; glow bật khi blink
        boxShadow: isBlink && item.isAlerted ? boxShadow : baseStroke
      }}
      className={twMerge('flex h-12 w-12 items-center justify-center rounded-full bg-white transition-all')}
    >
      <PhoneFilledIcon
        style={{
          color: strokeColor,
          // Bóng của icon cho cảm giác nổi phía trước vùng glow.
          filter: `drop-shadow(0 3px 6px ${shadowStrong})`
        }}
        className='h-6 w-6'
      />
    </div>
  );
};

const AICallItem: React.FC<AICallItemProps> = ({ item, selected, onSelect }) => {
  const optionData = item.isAlerted
    ? CALL_SESSION_OPTION[CALL_SESSION_TYPE.ERROR]
    : CALL_SESSION_OPTION[CALL_SESSION_TYPE.NORMAL];
  const sourceData = item.callSource ? CALL_SOURCE_OPTION[item.callSource as keyof typeof CALL_SOURCE_OPTION] : null;

  return (
    <div
      className={twMerge(
        'flex w-full cursor-pointer gap-4 px-5 py-3 hover:bg-colorBgItemHover',
        selected && 'bg-colorBgItemHover'
      )}
      onClick={onSelect}
    >
      <div>
        <AICallItemSymbol item={item} />
      </div>
      <div className='mt-[3px] flex-1'>
        <div className='mb-1 flex items-center gap-2'>
          <Typography.Text className='font-semibold leading-5'>{item.customerPhoneNumber}</Typography.Text>
          <span className='rounded-[3px] border px-1.5 text-xs uppercase leading-5' style={sourceData?.tagStye}>
            {item.callSource}
          </span>
        </div>
        <Typography.Paragraph className='mb-0 text-sm text-colorTextPlaceholder'>
          {item.lastAiAction?.content}
        </Typography.Paragraph>
      </div>
      <div className='mt-[3px]'>
        <Typography.Text
          className='text-end text-xs'
          style={{
            color: optionData?.textColor
          }}
        >
          {item.receivedAt ? dayjs(item.receivedAt).format('HH:mm') : ''}
        </Typography.Text>
      </div>
    </div>
  );
};

export default memo(AICallItem);
